import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, IconButton, Paper } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { CustomPagination, FunctionsEnum } from '@watter/shared-components';
import React, { useEffect, useRef, useState } from 'react';
import { GetCountries, GetState } from 'react-country-state-city';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CommonTable } from '../../components/common-styles';
import { CustomSlider } from '../../components/slider/slider';
import { usePermissions } from '../../custom_hooks/permissions';
import { GET_MANAGE_CUSTOMERS_LIST } from '../../graphql/queries';
import { CustomerData } from '../../interfaces/customer-interface';
import { client } from '../../service/apolloClient';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { NavigationConfig } from '../../utils/navigation-constants';
import { AddCustomer } from './addCustomer';
import { updateRefetchApiCall } from '../../store/reducers/apiCallSlice';

declare global {
    interface Window {
        handleAddCustomer?: () => void;
    }
}

export const ManageCustomer = () => {
    const { page: pageState } = useSelector((state: any) => state?.apiCall);
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const navigate = useNavigate();
    const [listData, setCustomerListData] = useState<CustomerData[]>([]);
    const dispatch = useDispatch();
    const [totalCount, setTotalRecordCount] = React.useState(0);
    const rowsPerPage = 10;
    const [page, setCurrentPage] = React.useState(pageState);
    const tableRef = useRef<HTMLInputElement>(null);
    const [filteredCountry, setFilteredCountry] = useState<any[]>([]);

    const handleEditClick = (params: GridRowParams, event: React.MouseEvent) => {
        event.stopPropagation();
        const row = params;
        navigate(NavigationConfig.EditCustomer, {
            state: { customerData: row },
        });
    };
    const { canEdit } = usePermissions();
    const columns: GridColDef[] = [
        {
            field: 'username',
            headerName: 'Customer Name',
            flex: 0.8,
            renderCell: params => {
                if (!params.value || params.value.trim() === '') {
                    return '-';
                }
                return params.value;
            },
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1,
            renderCell: params => {
                if (!params.value || params.value.trim() === '') {
                    return '-';
                }
                return params.value;
            },
        },
        {
            field: 'address',
            headerName: 'Address',
            flex: 1.2,
            renderCell: params => {
                if (!params.value || params.value.trim() === '') {
                    return '-';
                }
                return params.value;
            },
        },
        {
            field: 'serial_id',
            headerName: 'Serial ID',
            flex: 0.7,
            renderCell: params => {
                if (!params.value || params.value.trim() === '') {
                    return '-';
                }
                return params.value;
            },
        },
        {
            field: 'vast_id',
            headerName: 'Vast ID',
            flex: 0.7,
            renderCell: params => {
                if (!params.value || params.value.trim() === '') {
                    return '-';
                }
                return params.value;
            },
        },
        {
            field: 'status',
            headerName: 'User status',
            flex: 0.5,
            renderCell: params => {
                if (!params.value || params.value.trim() === '') {
                    return '-';
                }
                return params.value;
            },
        },
        ...(canEdit(FunctionsEnum.MANAGE_CUSTOMERS)
            ? [
                  {
                      field: 'actions',
                      headerName: 'Actions',
                      sortable: false,
                      flex: 0.4,
                      headerAlign: 'right' as const,
                      align: 'right' as const,
                      cellClassName: canEdit(FunctionsEnum.MANAGE_CUSTOMERS) ? 'cstRemovePointer' : '',
                      renderCell: (params: GridRenderCellParams) => (
                          <Box display="flex" justifyContent="end">
                              <IconButton
                                  disabled={!canEdit(FunctionsEnum.MANAGE_CUSTOMERS)}
                                  onClick={e => {
                                      e.preventDefault();
                                      handleEditClick(params.row, e);
                                  }}
                              >
                                  {' '}
                                  <EditOutlinedIcon
                                      sx={{
                                          '&.Mui-disabled': {
                                              opacity: 0.5,
                                              cursor: 'not-allowed',
                                          },
                                      }}
                                  />
                              </IconButton>
                          </Box>
                      ),
                  },
              ]
            : []),
    ];
    const handleRowClick = (params: GridRowParams) => {
        const row = params.row as CustomerData;
        navigate(NavigationConfig.CustomerDetails.replace(':id', row.id.toString()), {
            state: { customerData: row },
        });
        dispatch(
            updateRefetchApiCall({
                page,
            }),
        );
    };
    const getAddress = React.useCallback((...data: any) => {
        return [...data.filter(Boolean)].join(', ');
    }, []);
    useEffect(() => {
        const fetchFilteredCountry = async () => {
            try {
                const result = await GetCountries();
                setFilteredCountry(result);
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };

        fetchFilteredCountry();
    }, []);
    const fetchStateName = React.useCallback(
        async (countryId: string | undefined, stateCode: string | undefined): Promise<string> => {
            if (!countryId || !stateCode) return stateCode || '--';
            try {
                // Fix: Convert countryId to number for GetState
                const states = await GetState(Number(countryId));
                const matchedState = states.find((s: any) => s.state_code === stateCode);
                return matchedState?.name || stateCode;
            } catch {
                console.warn(`Failed to fetch states for countryId ${countryId}`);
                return stateCode;
            }
        },
        [],
    );

    const formatCustomer = React.useCallback(
        async (customer: any, filteredCountry: any[]): Promise<any> => {
            const address = customer?.user_addresses?.[0] || {};
            const countryCode = address.country;
            const stateCode = address.state;

            const matchedCountry = filteredCountry.find(c => c.iso2 === countryCode);
            const countryId = matchedCountry?.id;

            const stateName = await fetchStateName(countryId, stateCode);

            const formattedAddress = getAddress(address.street, address.city, stateName, address.zip_code);

            return {
                id: customer?.id,
                username: customer?.username,
                serial_id: customer?.machine_serial_ids?.[0],
                vast_id: customer?.machine_ids?.[0],
                email: customer?.email,
                status: customer?.status === 1 ? 'Active' : 'Inactive',
                address: formattedAddress,
            };
        },
        [fetchStateName, getAddress],
    );

    const fetchManageCustomerData = React.useCallback(async () => {
        try {
            dispatch(showLoader());

            const { data } = await client.query({
                query: GET_MANAGE_CUSTOMERS_LIST,
                variables: {
                    limit: rowsPerPage,
                    page,
                },
                fetchPolicy: 'network-only',
            });

            if (!data?.getManageCustomerList?.data) return;

            const customers = data.getManageCustomerList.data;

            if (customers?.length === 0) {
                dispatch(
                    openToast({
                        show: true,
                        message: t('no_customer_register_msg'),
                        severity: 'error',
                        isAlertOpen: true,
                    }),
                );
                return;
            }

            const finalList = await Promise.all(
                customers.map((customer: any) => formatCustomer(customer, filteredCountry)),
            );

            setCustomerListData(finalList);
            setTotalRecordCount(data?.getManageCustomerList?.total_count);
            dispatch(hideLoader());
            dispatch(
                updateRefetchApiCall({
                    page,
                }),
            );
        } catch (error) {
            dispatch(hideLoader());
            console.error('Error fetching customer data:', error);
            dispatch(
                openToast({
                    show: true,
                    message: t('unable_to_load_customer_data'),
                    severity: 'error',
                    isAlertOpen: true,
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
    }, [dispatch, filteredCountry, page, formatCustomer, t]);
    useEffect(() => {
        if (filteredCountry?.length > 0) {
            fetchManageCustomerData();
        }
    }, [filteredCountry, page, fetchManageCustomerData]);

    React.useEffect(() => {
        setCustomerListData([]);
        if (tableRef.current) {
            tableRef.current.scrollIntoView();
        }
    }, [page]);

    const handleAddCustomer = () => {
        setOpen(true);
    };
    useEffect(() => {
        window.handleAddCustomer = handleAddCustomer;

        return () => {
            delete window.handleAddCustomer;
        };
    }, []);

    return (
        <CommonTable height="calc(100vh - 200px)">
            <Paper sx={{ width: '100%' }}>
                <DataGrid
                    rows={listData}
                    columns={columns}
                    disableColumnMenu
                    disableColumnSorting
                    hideFooter
                    onRowClick={handleRowClick}
                    ref={tableRef}
                />
                {totalCount > 0 && (
                    <CustomPagination
                        totalCount={totalCount}
                        rowsPerPage={rowsPerPage}
                        currentPage={page}
                        onPageChange={setCurrentPage}
                    />
                )}
                <CustomSlider
                    sliderOpen={open}
                    sliderClose={() => setOpen(false)}
                    drawerList={
                        <AddCustomer
                            onClose={() => setOpen(false)}
                            fetchMangeInstallerData={fetchManageCustomerData}
                            open={open}
                        />
                    }
                />
            </Paper>
        </CommonTable>
    );
};
